// Unpublished Work © 2021-2026 Deere & Company.
import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {withRouter} from 'react-router-dom';
import Panel from 'Ui/components/panel/panel';
import NoDataMessage from 'Ui/components/panel/no-data-message';
import {useDeepMemo} from 'Utils/react-utils';
import OnLinkProgressBar from 'Ui/components/common/onlink-progress-bar';
import clsx from 'clsx';
import {redirect} from 'Utils/redirect-utils';
import Stack from '@mui/material/Stack';
import {Tooltip, Button, Box} from '@deere/isg.component-library';
import DataGridWrapper from '../../common/data-table/data-grid-wrapper';

const NUM_ROWS = 5;
const ONE_HUNDRED = 100;
const RED_LIMIT = 0.8;

function getThresholdRatio(part) {
    return (part.quantity + part.quantityOrdered) / part.thresholdLevel;
}

function getDataGridColumns(translations) {
    return [
        {
            headerName: translations.NAME,
            field: 'name',
            renderCell: (params) => {
                const {name} = params.row;

                return (
                    <Tooltip
                        placement='bottom'
                        title={name}
                    >
                        <span className='basic-table-cell-block'>{name}</span>
                    </Tooltip>
                );
            }
        },
        {
            headerName: `${translations.ONLINK_PART} #`,
            field: 'partNumber',
            width: 100,
            renderCell: (params) => {
                const {partNumber} = params.row;

                return (
                    <Tooltip
                        placement='bottom'
                        title={partNumber}
                    >
                        <span className='basic-table-cell-block'>
                            {partNumber}
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            headerName: translations.ONLINK_STOCK,
            type: 'number',
            field: 'quantity',
            width: 80
        },
        {
            headerName: translations.ONLINK_THRESHOLD,
            field: 'thresholdLevel',
            type: 'number',
            width: 110
        },
        {
            headerName: '',
            field: 'thresholdRatio',
            width: 80,
            renderCell: (params) => {
                const barColorRed = params.row.thresholdRatio < RED_LIMIT;

                return (
                    <Box
                        alignItems='center'
                        display='flex'
                        height='100%'
                        justifyContent='center'
                        width='100%'
                    >
                        <OnLinkProgressBar
                            barClassName='low-inventory-progress-bar'
                            fillClassName={clsx('onlink-progress-bar-fill', {
                                red: barColorRed,
                                yellow: !barColorRed
                            })}
                            percentFilled={ONE_HUNDRED}
                        />
                    </Box>
                );
            }
        }
    ];
}

function createRows(partBins) {
    if (partBins.length === 0) {
        return [];
    }

    const reducedPartBins = partBins.reduce((reducedPartBins, partBin) => {
        const thresholdRatio = getThresholdRatio(partBin);

        if (partBin.thresholdLevel > 0 && thresholdRatio < 1) {
            reducedPartBins.push({
                ...partBin,
                thresholdRatio
            });
        }

        return reducedPartBins;
    }, []);

    const sortedPartBins = reducedPartBins.sort(
        (a, b) => a.thresholdRatio - b.thresholdRatio
    );

    return sortedPartBins.slice(0, NUM_ROWS);
}

function LowInventory(props) {
    const {
        history,
        tile: {partBins},
        translations
    } = props;

    const rows = useDeepMemo(() => createRows(partBins), [partBins]);

    const dataComponent = (
        <DataGridWrapper
            columns={getDataGridColumns(translations)}
            getRowId={(row) => row.partBinId}
            initialState={{
                sorting: {
                    sortModel: [
                        {
                            field: 'thresholdRatio',
                            sort: 'asc'
                        }
                    ]
                }
            }}
            noDataAvailableMessage={translations.NO_EQUIPMENT_FOUND}
            rows={rows}
            slotProps={{
                root: {
                    disableColumnMenu: true
                }
            }}
            translations={translations}
        />
    );

    return (
        <Panel
            title={translations.ONLINK_LOW_INVENTORY_PARTS}
            translations={translations}
        >
            <div>
                {rows.length > 0 ?
                    dataComponent :
                    (
                        <NoDataMessage translations={translations}/>
                    )}
                <Stack
                    alignItems='center'
                    direction='row'
                    justifyContent='flex-end'
                    width='100%'
                >
                    <Button
                        onClick={() =>
                            redirect('/equipment/parts/orders', history)
                        }
                        sx={{
                            fontWeight: 'bold'
                        }}
                        variant='tertiary'
                    >
                        {`${translations.ORDERS} >`}
                    </Button>
                </Stack>
            </div>
        </Panel>
    );
}

LowInventory.propTypes = {
    history: PropTypes.history,
    tile: PropTypes.object,
    translations: PropTypes.translations
};

export default withRouter(LowInventory);
